'use client';

import { History } from '@repo/types';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@repo/ui/components/ui/accordion';
import { Badge } from '@repo/ui/components/ui/badge';
import { createFieldResolver } from '@repo/ui/lib/utils/field-label-resolver';
import { Clock, ChevronDown } from 'lucide-react';
import { useMemo } from 'react';
import { HistoryTravauxContent } from './history-travaux-content';

interface HistoryItemProps {
  history: History;
  index: number;
  champignons: any[];
  nuisibles: any[];
  especes: any[];
}

function formatDate(dateString: string | Date | null | undefined): string {
  if (!dateString) return 'Date inconnue';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch {
    return 'Date invalide';
  }
}

export function HistoryItem({ history, index, champignons, nuisibles, especes }: HistoryItemProps) {
  // Create resolver for this history item
  const resolver = useMemo(() => {
    // Create a temporary inventaire object with history data as arbre
    const tempInventaire = {
      arbre: history
    };
    return createFieldResolver(tempInventaire, { champignons, nuisibles, especes });
  }, [history, champignons, nuisibles, especes]);

  return (
    <AccordionItem
      key={history.id}
      value={`history-${history.id}`}
      className="border border-gray-200 rounded-lg bg-white shadow-sm hover:shadow-md transition-all duration-200 overflow-hidden"
    >
      <AccordionTrigger className="px-5 py-4 hover:no-underline hover:bg-gray-50/50 transition-colors duration-200 [&[data-state=open]]:bg-gray-50/50">
        <div className="flex items-center justify-between w-full mr-4">
          <div className="flex items-center gap-4">
            <Badge variant="outline" className="text-xs font-semibold px-2.5 py-1 bg-primary/5 border-primary/20 text-primary">
              #{index + 1}
            </Badge>
            <div className="flex items-center gap-2 text-left">
              <Clock className="h-4 w-4 text-muted-foreground" />
              <div>
                <p className="font-semibold text-sm text-gray-900">
                  Modification du {formatDate(history.created_at)}
                </p>
                {history.user_edited_date_travaux && (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Travaux prévus le {new Date(history.user_edited_date_travaux).toLocaleDateString('fr-FR')}
                  </p>
                )}
              </div>
            </div>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/trigger:rotate-180" />
        </div>
      </AccordionTrigger>

      <AccordionContent className="px-5 pb-5 pt-2 data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up">
        <HistoryTravauxContent history={history} resolver={resolver} />
      </AccordionContent>
    </AccordionItem>
  );
}
